
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { notifyOttoRouteChange } from "./utils/ottoSeoUtils";
import { SocialCrawlerHandler } from "./utils/crawlerUtils";
import RedirectHandler from "./components/RedirectHandler";
import { generateCityIndustryRoutes } from "./components/CityIndustryRouteHandler";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import WebsiteDesign from "./pages/services/WebsiteDesign";
import Photography from "./pages/services/Photography";
import Marketing from "./pages/services/Marketing";
import SEOServices from "./pages/services/SEOServices";
import Branding from "./pages/services/Branding";
import Software from "./pages/services/Software";
import RealEstateMedia from "./pages/services/RealEstateMedia";
import TechCoaching from "./pages/services/TechCoaching";
import VirtualTours from "./pages/services/VirtualTours";
import LEDPrinting from "./pages/services/LEDPrinting";
import Blog from "./pages/blog/Blog";
import BlogPost from "./pages/blog/BlogPost";
import Portfolio from "./pages/Portfolio";
import About from "./pages/About";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import BatonRougeLocation from "./pages/BatonRougeLocation";
import GonzalesLocation from "./pages/GonzalesLocation";
import PrairievilleLocation from "./pages/PrairievilleLocation";
import AscensionParishLocation from "./pages/AscensionParishLocation";
import GeismarLocation from "./pages/GeismarLocation";
import DutchtownLocation from "./pages/DutchtownLocation";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import WebDesignLanding from "./pages/landing/WebDesignLanding";
import WebDesignDirectories from "./pages/landing/WebDesignDirectories";
import WebDesignService from "./pages/landing/WebDesignService";
import DynamicSitemap from "./pages/blog/DynamicSitemap";
import Sitemap from "./pages/Sitemap";
import FacebookAdLanding from "./pages/landing/FacebookAdLanding";

function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const currentTitle = document.title;
    const currentDesc = document.querySelector('meta[name="description"]')?.getAttribute('content') || 
                        "Professional websites, content & marketing solutions by Astrem in Baton Rouge, LA. Transform your digital presence - contact us today!";
    
    notifyOttoRouteChange({
      path: pathname,
      title: currentTitle,
      url: window.location.href,
      description: currentDesc
    });
    
    console.log('Page navigation:', pathname, {
      title: currentTitle, 
      description: currentDesc
    });
  }, [pathname]);
  
  return null;
}

declare global {
  interface Window {
    dataLayer: any[];
  }
}

const App = () => {
  const queryClient = new QueryClient();
  
  // Pre-generate all the dynamic city-industry routes
  const cityIndustryRoutes = generateCityIndustryRoutes();
  
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <ScrollToTop />
          <RedirectHandler />
          <SocialCrawlerHandler />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/web-design-services" element={<WebDesignService />} />
            <Route path="/services/website-design" element={<WebsiteDesign />} />
            <Route path="/services/photography" element={<Photography />} />
            <Route path="/services/marketing" element={<Marketing />} />
            <Route path="/services/seo" element={<SEOServices />} />
            <Route path="/services/branding" element={<Branding />} />
            <Route path="/services/software" element={<Software />} />
            <Route path="/services/real-estate-media" element={<RealEstateMedia />} />
            <Route path="/services/tech-coaching" element={<TechCoaching />} />
            <Route path="/services/virtual-tours" element={<VirtualTours />} />
            <Route path="/services/led-printing" element={<LEDPrinting />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/blog/sitemap" element={<DynamicSitemap />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/locations/baton-rouge" element={<BatonRougeLocation />} />
            <Route path="/locations/gonzales" element={<GonzalesLocation />} />
            <Route path="/locations/prairieville" element={<PrairievilleLocation />} />
            <Route path="/locations/ascension-parish" element={<AscensionParishLocation />} />
            <Route path="/locations/geismar" element={<GeismarLocation />} />
            <Route path="/locations/dutchtown" element={<DutchtownLocation />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/web-design" element={<WebDesignLanding />} />
            <Route path="/web-design/directories" element={<WebDesignDirectories />} />
            <Route path="/tech-coaching" element={<TechCoaching />} />
            <Route path="/virtual-tours" element={<VirtualTours />} />
            <Route path="/led-printing" element={<LEDPrinting />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/facebook-ad-landing" element={<FacebookAdLanding />} />
            
            {/* Add the explicit city-industry routes */}
            {cityIndustryRoutes}
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
        <Sonner />
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
